import React from 'react';
import moment from 'moment';
import {
  ReprintWorkerCardMutation,
  useCreateAndPrintWorkerCardMutation,
  useReprintWorkerCardMutation,
  useUpdateWorkerMutation,
  useUploadSingleFileMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError, hasGraphQLErrorMessage, MatchBy } from 'utils/error';
import { uploadCompressedFile, useIsMounted } from 'utils';
import { RequiredField } from 'utils/validation';
import { PrintIcon } from 'components/icons';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { useJobsitePrinterOptions } from 'containers/utils';
import { Worker, WorkerTabProps, CardPrintJob } from 'containers/worker/types';
import { usePvcCardFormatOptions } from 'containers/worker/helpers/usePvcCardFormatOptions';
import {
  getFormDefaultValues,
  getFormInputs,
  getWorkerUpdateInput,
  useJobsiteBadgeTemplateOptions,
} from './PrintBadgeModalContent.forms';
import { CreateCardPrintJobFormData } from './types';

type PrintedWorkerCard =
  ReprintWorkerCardMutation['reprintWorkerCard']['workerCard'];

export interface PrintBadgeModalContentProps {
  setOpen: () => void;
  onAction: (cardPrintJob: CardPrintJob) => void;
  jobsiteId: string;
  worker: Worker;
  workerCardIdToReprint?: string;
  updateWorkerState: WorkerTabProps['updateWorkerState'];
}

const instructions =
  `Please review the following worker data. ` +
  `Any changes made here will be saved to the worker's profile.`;

export function PrintBadgeModalContent(
  props: PrintBadgeModalContentProps,
): React.ReactElement {
  const {
    workerCardIdToReprint,
    worker,
    jobsiteId,
    setOpen,
    onAction,
    updateWorkerState,
  } = props;
  const { workerId } = worker ?? {};
  const {
    trade: defaultTrade,
    jobTitle: defaultJobTitle,
    jobsiteContractor,
  } = worker?.jobsiteWorkers?.edges.find(
    (edge) => edge.node.jobsiteContractor?.jobsite?.jobsiteId === jobsiteId,
  )?.node ?? {};

  const { defaultJobsitePrinterId } = jobsiteContractor?.jobsite ?? {};

  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const isMounted = useIsMounted();

  /* When a new profile picture is dropped in the modal, we have to upload it first */
  const [uploadFile] = useUploadSingleFileMutation();

  /* If worker data such as trade is updated, we need to update the worker before printing */
  const [updateWorker] = useUpdateWorkerMutation();

  /* When the above are either avoided or executed, we can then create the print job */
  const [createAndPrintWorkerCard] = useCreateAndPrintWorkerCardMutation();

  const [reprintWorkerCard] = useReprintWorkerCardMutation();

  const { pvcCardFormatOptions } = usePvcCardFormatOptions(jobsiteId);
  const [defaultPvcCardFormat] = pvcCardFormatOptions ?? [];

  const printWorkerCard = async (
    data: CreateCardPrintJobFormData,
  ): Promise<PrintedWorkerCard> => {
    const { trade, jobTitle, jobsitePrinter, badgeTemplateName } = data ?? {};
    const jobsitePrinterId = jobsitePrinter.value;
    const cardTemplateName = badgeTemplateName.value;

    if (workerCardIdToReprint) {
      const result = await reprintWorkerCard({
        variables: {
          input: {
            id: workerCardIdToReprint,
            cardPrintJobInput: {
              jobsitePrinterId,
              cardTemplateName,
              trade,
              jobTitle,
            },
          },
        },
      });
      return result?.data?.reprintWorkerCard?.workerCard;
    }

    const result = await createAndPrintWorkerCard({
      variables: {
        input: {
          workerId,
          jobsiteId,
          workerCardInput: {
            workerCardFormatId: defaultPvcCardFormat?.value,
            startDate: moment().toDate(),
          },
          cardPrintJobInput: {
            jobsitePrinterId,
            cardTemplateName,
            trade,
            jobTitle,
          },
        },
      },
    });
    return result?.data?.createAndPrintWorkerCard?.workerCard;
  };

  /* Combines the above to add a new badge with any new updated data */
  const onSubmit: FormOnSubmit<CreateCardPrintJobFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    try {
      const { profilePictureFile } = data ?? {};
      let profilePictureFileId: string;
      if (profilePictureFile) {
        try {
          profilePictureFileId = await uploadCompressedFile(
            profilePictureFile,
            uploadFile,
          );
        } catch (error) {
          AlertService.alert(
            'danger',
            'Error uploading profile picture',
            getGraphQLError(error),
          );
          return;
        }
      }

      const workerInput = getWorkerUpdateInput(profilePictureFileId);
      if (workerInput) {
        try {
          const updateWorkerResult = await updateWorker({
            variables: { workerId, workerInput },
          });
          const { profilePictureCropped } =
            updateWorkerResult.data.updateWorker;
          updateWorkerState?.({ profilePictureCropped });
        } catch (error) {
          AlertService.alert(
            'danger',
            'Error updating worker data!',
            getGraphQLError(error),
          );
          return;
        }
      }

      try {
        const workerCard = await printWorkerCard(data);
        const { lastPrintJob } = workerCard ?? {};
        const cardPrintJob: CardPrintJob = {
          ...lastPrintJob,
          workerCard,
          jobsite: { jobsiteId },
        };
        onAction(cardPrintJob);
      } catch (error) {
        if (
          hasGraphQLErrorMessage(
            error,
            'Cannot crop profile image for worker',
            MatchBy.startOfMessage,
          )
        ) {
          formApi.setError('profilePictureFile', {
            message: RequiredField`Photo`,
            shouldFocus: true,
          });
        } else {
          AlertService.alert(
            'danger',
            'Error sending card print request',
            getGraphQLError(error),
          );
        }
      }
    } finally {
      if (isMounted()) setIsFetching(false);
    }
  };

  const workerPhotoUrl = worker?.profilePictureCropped?.downloadUrl;
  const badgeTemplateOptions = useJobsiteBadgeTemplateOptions(jobsiteId);
  const { jobsitePrinterOptions } = useJobsitePrinterOptions(jobsiteId);

  const formInputs = React.useMemo(
    () =>
      getFormInputs({
        workerPhotoUrl,
        badgeTemplateOptions,
        jobsitePrinterOptions,
      }),
    [workerPhotoUrl, badgeTemplateOptions, jobsitePrinterOptions],
  );
  const defaultValues = React.useMemo(
    () =>
      getFormDefaultValues({
        defaultTrade,
        defaultJobTitle,
        defaultJobsitePrinterId,
        badgeTemplateOptions,
        jobsitePrinterOptions,
      }),
    [
      defaultTrade,
      defaultJobTitle,
      defaultJobsitePrinterId,
      badgeTemplateOptions,
      jobsitePrinterOptions,
    ],
  );

  return (
    <ModalFormContent
      setOpen={setOpen}
      renderAbove={<p className="odin-mb-4">{instructions}</p>}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText={`${workerCardIdToReprint ? 'Reprint' : 'Print'} badge`}
      actionIcon={PrintIcon}
      actionButtonWithSpinner={isFetching}
    />
  );
}
